import { motion } from "framer-motion";
import { useFactors } from "../state/FactorsContext";
import { toSuperscript } from "../lib/format";

/**
 * A vertical scale of the collapse. The top is the starting planet count
 * (~10²⁴); the bottom is a floor of 10⁻³⁰. A glowing marker shows where the
 * running total currently sits, and a dashed line marks "= 1" — the threshold
 * below which we are, in expectation, alone.
 */
const FLOOR = -30;

export default function CollapseMeter() {
  const { result } = useFactors();

  const startExp = Math.log10(Math.max(1, result.start));
  const curExp =
    result.total > 0 ? Math.log10(result.total) : FLOOR;
  const clampCur = Math.max(FLOOR, Math.min(startExp, curExp));

  const span = startExp - FLOOR;
  // 0% = top (start), 100% = bottom (floor).
  const markerTop = ((startExp - clampCur) / span) * 100;
  const oneTop = ((startExp - 0) / span) * 100;

  const expLabel = (e: number) =>
    `10${toSuperscript(Math.round(e))}`;

  return (
    <div className="flex h-44 items-stretch gap-3">
      <div className="relative w-1.5 rounded-full bg-white/10">
        {/* Filled portion from top down to the marker. */}
        <motion.div
          className="absolute left-0 top-0 w-full rounded-full bg-gradient-to-b from-cyan/80 to-gold/80"
          initial={false}
          animate={{ height: `${markerTop}%` }}
          transition={{ type: "spring", stiffness: 120, damping: 22 }}
        />
        {/* "= 1" threshold line. */}
        <div
          className="absolute -left-1 w-3.5 border-t border-dashed border-white/40"
          style={{ top: `${oneTop}%` }}
        />
        {/* Current marker. */}
        <motion.div
          className="absolute -left-[5px] h-3 w-3 rounded-full bg-gold shadow-[0_0_12px_rgba(255,206,110,0.9)]"
          initial={false}
          animate={{ top: `calc(${markerTop}% - 6px)` }}
          transition={{ type: "spring", stiffness: 120, damping: 22 }}
        />
      </div>

      <div className="relative flex-1 text-[11px] leading-none">
        <span
          className="absolute left-0 font-mono text-white/55"
          style={{ top: 0 }}
        >
          {expLabel(startExp)} start
        </span>
        <span
          className="absolute left-0 -translate-y-1/2 font-mono text-white/45"
          style={{ top: `${oneTop}%` }}
        >
          1 · not alone
        </span>
        <span
          className="absolute left-0 -translate-y-1/2 font-mono text-gold"
          style={{ top: `calc(${Math.min(markerTop, 94)}% )` }}
        >
          {expLabel(clampCur)} now
        </span>
      </div>
    </div>
  );
}
